import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import type { BetaAccessRequestResponse } from '@/lib/api';

interface UseBetaAccessRequestsOptions {
  status?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

interface UseBetaAccessRequestsReturn {
  requests: BetaAccessRequestResponse[];
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBetaAccessRequests(
  options: UseBetaAccessRequestsOptions = {},
): UseBetaAccessRequestsReturn {
  const [requests, setRequests] = useState<BetaAccessRequestResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;

    async function fetchRequests() {
      setIsLoading(true);
      setError(null);

      try {
        const result = await api.listBetaAccessRequests({
          status: options.status || undefined,
          search: options.search || undefined,
          limit: options.limit,
          offset: options.offset,
        });

        if (!cancelled) {
          setRequests(result.requests);
          setTotal(result.total);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load requests');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchRequests();

    return () => {
      cancelled = true;
    };
  }, [options.status, options.search, options.limit, options.offset, refreshKey]);

  return { requests, total, isLoading, error, refetch };
}
